/**
 * Workbook Brain Chat Component
 * Interface conversationnelle pour poser des questions sur le classeur
 */

import React, { useState, useRef, useEffect } from 'react';
import { LoadingSpinner } from '../common/LoadingSpinner';
import type { WorkbookBrainChatMessage } from '@excel-ai-studio/shared';
import { getWorkbookData } from '../../services/workbook-brain-api.service';
import { applyChatSuggestion } from '../../services/workbook-brain-writer.service';
import { getSettings } from '../../store/settings.store';

interface WorkbookBrainChatProps {
  onMessageSent?: (message: string) => void;
}

/**
 * Composant Chat du Cerveau Classeur
 */
export const WorkbookBrainChat: React.FC<WorkbookBrainChatProps> = ({ onMessageSent }) => {
  const [messages, setMessages] = useState<WorkbookBrainChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [lastAssistantMessage, setLastAssistantMessage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /**
   * Crée une session de chat avec le backend
   */
  const ensureSession = async (): Promise<string> => {
    if (sessionId) return sessionId;

    const workbookData = await getWorkbookData();
    const settings = getSettings();
    const response = await fetch('/api/workbook-brain/chat/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        workbookData,
        provider: settings.provider,
        model: settings.model,
        apiKey: settings.apiKey,
      }),
    });

    if (!response.ok) {
      throw new Error('Impossible de créer la session de chat');
    }

    const data = await response.json();
    setSessionId(data.sessionId);
    return data.sessionId;
  };

  const sendMessage = async (overrideMessage?: string) => {
    const messageText = overrideMessage ?? inputValue;
    if (!messageText.trim()) return;

    setError(null);
    setIsLoading(true);
    setInputValue('');

    try {
      const userMessage: WorkbookBrainChatMessage = {
        id: `msg_${Date.now()}`,
        role: 'user',
        content: messageText,
        timestamp: new Date() as any,
      };

      setMessages((prev) => [...prev, userMessage]);
      onMessageSent?.(messageText);

      const sid = await ensureSession();

      const settings = getSettings();
      const response = await fetch('/api/workbook-brain/chat/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sid,
          message: messageText,
          provider: settings.provider,
          model: settings.model,
          apiKey: settings.apiKey,
        }),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi du message');
      }

      const data = await response.json();

      const assistantMessage: WorkbookBrainChatMessage = {
        id: `msg_${Date.now()}_response`,
        role: 'assistant',
        content: data.response,
        timestamp: new Date() as any,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setLastAssistantMessage(data.response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible d\'envoyer le message');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplySuggestion = async () => {
    if (!lastAssistantMessage) return;
    
    try {
      setMessage(null);
      await applyChatSuggestion(lastAssistantMessage);
      setMessage({ type: 'success', text: '✅ Suggestion appliquée avec succès' });
      setTimeout(() => setMessage(null), 5000);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erreur lors de l\'application de la suggestion';
      setMessage({ type: 'error', text: `❌ ${errorMsg}` });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '12px' }}>
      {/* Conteneur des messages */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          padding: '12px',
          backgroundColor: '#f9f9f9',
          borderRadius: '4px',
          border: '1px solid #ddd',
          minHeight: '300px',
        }}
      >
        {messages.length === 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#999',
              fontSize: '13px',
              textAlign: 'center',
            }}
          >
            <div>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>🧠</div>
              <div>Posez-moi une question sur votre classeur !</div>
              <div style={{ fontSize: '11px', marginTop: '8px', color: '#bbb' }}>
                Essayez : "Qu'est-ce que ce classeur ?" ou "Quels risques ont été détectés ?"
              </div>
            </div>
          </div>
        )}

        {messages.map((message: any) => (
          <div
            key={message.id}
            style={{
              display: 'flex',
              justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: '8px',
            }}
          >
            <div
              style={{
                maxWidth: '80%',
                padding: '10px 12px',
                borderRadius: '8px',
                backgroundColor: message.role === 'user' ? '#2196F3' : '#e0e0e0',
                color: message.role === 'user' ? 'white' : '#333',
                fontSize: '13px',
                lineHeight: '1.5',
                wordWrap: 'break-word',
              }}
            >
              {message.content.split('\n').map((line: string, idx: number) => (
                <div key={idx}>
                  {line.startsWith('**') && line.endsWith('**') ? (
                    <strong>{line.replace(/\*\*/g, '')}</strong>
                  ) : (
                    line
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {isLoading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div
              style={{
                padding: '10px 12px',
                borderRadius: '8px',
                backgroundColor: '#e0e0e0',
                color: '#333',
              }}
            >
              <LoadingSpinner label="Réflexion en cours..." />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Affichage des erreurs */}
      {error && (
        <div
          style={{
            padding: '8px 12px',
            backgroundColor: '#ffebee',
            color: '#c62828',
            borderRadius: '4px',
            fontSize: '12px',
          }}
        >
          ❌ {error}
        </div>
      )}

      {message && (
        <div
          style={{
            padding: '8px 12px',
            backgroundColor: message.type === 'success' ? '#e8f5e9' : '#ffebee',
            color: message.type === 'success' ? '#2e7d32' : '#c62828',
            borderRadius: '4px',
            fontSize: '12px',
          }}
        >
          {message.text}
        </div>
      )}

      {/* Zone de saisie */}
      <div style={{ display: 'flex', gap: '8px' }}>
        <textarea
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Posez une question sur votre classeur... (Maj+Entrée pour nouvelle ligne)"
          style={{
            flex: 1,
            padding: '10px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '13px',
            fontFamily: 'inherit',
            resize: 'none',
            minHeight: '60px',
            maxHeight: '120px',
          }}
          disabled={isLoading}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <button
            onClick={() => sendMessage()}
            disabled={isLoading || !inputValue.trim()}
            style={{
              padding: '10px 16px',
              backgroundColor: isLoading || !inputValue.trim() ? '#ccc' : '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isLoading || !inputValue.trim() ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: 'bold',
            }}
          >
            Envoyer
          </button>
          {lastAssistantMessage && (
            <button
              onClick={handleApplySuggestion}
              disabled={isLoading}
              style={{
                padding: '6px 12px',
                backgroundColor: isLoading ? '#ccc' : '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '11px',
                fontWeight: 'bold',
              }}
            >
              ✓ Appliquer
            </button>
          )}
        </div>
      </div>

      {/* Questions rapides */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', fontSize: '11px' }}>
        {[
          'Qu\'est-ce que ce classeur ?',
          'Quels risques détectés ?',
          'Score de performance ?',
          'Conseils d\'optimisation ?',
        ].map((question, idx) => (
          <button
            key={idx}
            onClick={() => {
              sendMessage(question);
            }}
            disabled={isLoading}
            style={{
              padding: '4px 8px',
              backgroundColor: '#f0f0f0',
              color: '#666',
              border: '1px solid #ddd',
              borderRadius: '3px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '11px',
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            {question}
          </button>
        ))}
      </div>
    </div>
  );
};
